import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/auth.service';
import settingsService from '../services/settings.service';
import { userService, User } from '../services/user.service';

const Settings: React.FC = () => {
  const { loading: authLoading, user } = useAuth();
  const isAdmin =
    authService.hasRole('admin') ||
    user?.roles?.some((role: { name: string }) => role.name === 'admin') ||
    false;

  const [emailCcRecipients, setEmailCcRecipients] = useState('');
  const [reportRecipients, setReportRecipients] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [appUsers, setAppUsers] = useState<User[]>([]);
  const [calendarUsers, setCalendarUsers] = useState<string[]>([]);
  const [availabilityMode, setAvailabilityMode] = useState<'any' | 'all'>('any');
  const [userSearch, setUserSearch] = useState('');
  const [extraEmail, setExtraEmail] = useState('');
  const [savingCalendar, setSavingCalendar] = useState(false);

  useEffect(() => {
    if (authLoading || !isAdmin) {
      if (!authLoading && !isAdmin) {
        setLoading(false);
      }
      return;
    }

    const loadSettings = async () => {
      setLoading(true);
      try {
        const emailResponse = await settingsService.getEmailSettings();
        setEmailCcRecipients(emailResponse.data.email_cc_recipients || '');
        setReportRecipients(emailResponse.data.weekly_pending_report_recipients || '');

        try {
          const calendarResponse = await settingsService.getCalendarSettings();
          setCalendarUsers(
            (calendarResponse.data.calendar_users || []).map(email => email.toLowerCase())
          );
          setAvailabilityMode(
            calendarResponse.data.availability_mode === 'all' ? 'all' : 'any'
          );
        } catch (calendarError) {
          console.error('Failed to load calendar settings:', calendarError);
        }

        try {
          const usersResponse = await userService.getUsers();
          setAppUsers(usersResponse.data.data || []);
        } catch (usersError) {
          console.error('Failed to load users for calendar settings:', usersError);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [authLoading, isAdmin]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsService.updateEmailSettings({
        email_cc_recipients: emailCcRecipients,
        weekly_pending_report_recipients: reportRecipients,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleCalendarUser = (email: string) => {
    const normalized = email.trim().toLowerCase();
    setCalendarUsers(prev =>
      prev.includes(normalized)
        ? prev.filter(item => item !== normalized)
        : [...prev, normalized]
    );
  };

  const addExtraEmail = () => {
    const normalized = extraEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      toast.error('Enter a valid email address');
      return;
    }
    setCalendarUsers(prev => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setExtraEmail('');
  };

  const handleSaveCalendar = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCalendar(true);
    try {
      const response = await settingsService.updateCalendarSettings({
        calendar_users: calendarUsers,
        availability_mode: availabilityMode,
      });
      setCalendarUsers(response.data.calendar_users || []);
      setAvailabilityMode(response.data.availability_mode === 'all' ? 'all' : 'any');
      toast.success('Calendar settings saved');
    } catch (error) {
      console.error('Failed to save calendar settings:', error);
      toast.error('Failed to save calendar settings');
    } finally {
      setSavingCalendar(false);
    }
  };

  const knownEmails = useMemo(
    () => new Set(appUsers.map(user => user.email.toLowerCase())),
    [appUsers]
  );

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const matches = appUsers.filter(user => {
      if (!query) return true;
      const roles = user.roles?.map(role => role.name).join(' ') || '';
      return (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        roles.includes(query)
      );
    });
    return [...matches].sort((a, b) => {
      const aExpert = a.roles?.some(role => role.name === 'expert_user') ? 0 : 1;
      const bExpert = b.roles?.some(role => role.name === 'expert_user') ? 0 : 1;
      if (aExpert !== bExpert) return aExpert - bExpert;
      return a.name.localeCompare(b.name);
    });
  }, [appUsers, userSearch]);

  const extraCalendars = calendarUsers.filter(email => !knownEmails.has(email));

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const response = await settingsService.sendPendingCasesReport();
      toast.success(response.data.message || 'Report sent');
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || 'Failed to send the report');
    } finally {
      setSendingReport(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className='row'>
        <div className='col-12'>
          <p className='text-muted'>Loading settings…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to='/dashboard' replace />;
  }

  return (
    <div className='row'>
      <div className='col-12'>
        <h1 className='page-title mb-4'>Settings</h1>
      </div>

      <div className='col-lg-8'>
        <div className='card mb-4'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Calendar slots</h3>
          </div>
          <div className='card-body'>
            <p className='text-muted mb-4'>
              Choose the Microsoft 365 mailboxes used when someone loads meeting
              slots. Pick the expert users (or any other mailbox in your tenant).
              Leave everyone unchecked to use the signed-in user&apos;s calendar.
            </p>
            <form onSubmit={handleSaveCalendar}>
              <div className='mb-3'>
                <label htmlFor='calendar-user-search' className='form-label'>
                  Calendars to read
                </label>
                <input
                  id='calendar-user-search'
                  type='search'
                  className='form-control mb-2'
                  placeholder='Search by name, email, or role'
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                />
                <div
                  className='border rounded p-2'
                  style={{ maxHeight: 320, overflowY: 'auto' }}
                >
                  {filteredUsers.length === 0 ? (
                    <p className='text-muted mb-0 px-2 py-1'>No users match that search.</p>
                  ) : (
                    filteredUsers.map(appUser => {
                      const email = appUser.email.toLowerCase();
                      const isExpert = appUser.roles?.some(role => role.name === 'expert_user');
                      return (
                        <label
                          key={appUser.id}
                          className='d-flex align-items-start gap-2 px-2 py-2 mb-0'
                          htmlFor={`calendar-user-${appUser.id}`}
                        >
                          <input
                            id={`calendar-user-${appUser.id}`}
                            className='form-check-input mt-1'
                            type='checkbox'
                            checked={calendarUsers.includes(email)}
                            onChange={() => toggleCalendarUser(email)}
                          />
                          <span>
                            <span className='d-block'>
                              {appUser.name}
                              {isExpert && (
                                <span className='badge bg-warning text-dark ms-2'>Expert</span>
                              )}
                            </span>
                            <span className='text-muted small'>{appUser.email}</span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                <div className='form-text'>
                  {calendarUsers.length === 0
                    ? 'No calendars selected. Slot lookup will use the signed-in user.'
                    : `${calendarUsers.length} calendar${calendarUsers.length === 1 ? '' : 's'} selected.`}
                </div>
              </div>

              <div className='mb-3'>
                <label htmlFor='extra-calendar-email' className='form-label'>
                  Add a mailbox that is not an app user
                </label>
                <div className='input-group'>
                  <input
                    id='extra-calendar-email'
                    type='email'
                    className='form-control'
                    placeholder='expert@yourtenant.com'
                    value={extraEmail}
                    onChange={e => setExtraEmail(e.target.value)}
                  />
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={addExtraEmail}
                  >
                    Add
                  </button>
                </div>
                {extraCalendars.length > 0 && (
                  <div className='mt-2 d-flex flex-wrap gap-2'>
                    {extraCalendars.map(email => (
                      <span key={email} className='badge bg-light text-dark border'>
                        {email}
                        <button
                          type='button'
                          className='btn btn-sm btn-link p-0 ms-2'
                          onClick={() => toggleCalendarUser(email)}
                          aria-label={`Remove ${email}`}
                        >
                          Remove
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <fieldset className='mb-3'>
                <legend className='form-label fs-6'>When to offer a time</legend>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='availability-mode'
                    id='availability-any'
                    checked={availabilityMode === 'any'}
                    onChange={() => setAvailabilityMode('any')}
                  />
                  <label className='form-check-label' htmlFor='availability-any'>
                    At least one selected calendar is free
                  </label>
                </div>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='availability-mode'
                    id='availability-all'
                    checked={availabilityMode === 'all'}
                    onChange={() => setAvailabilityMode('all')}
                  />
                  <label className='form-check-label' htmlFor='availability-all'>
                    Every selected calendar is free
                  </label>
                </div>
              </fieldset>

              <button type='submit' className='btn btn-primary' disabled={savingCalendar}>
                {savingCalendar ? 'Saving…' : 'Save calendar settings'}
              </button>
            </form>
          </div>
        </div>

        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Email Settings</h3>
          </div>
          <div className='card-body'>
            <p className='text-muted mb-4'>
              Add intake app user email addresses to CC on all outgoing emails to law firms.
              Separate multiple addresses with commas or new lines.
            </p>
            <form onSubmit={handleSave}>
              <div className='mb-3'>
                <label htmlFor='email-cc-recipients' className='form-label'>
                  Email CC Recipients
                </label>
                <textarea
                  id='email-cc-recipients'
                  className='form-control'
                  rows={4}
                  placeholder='user1@example.com, user2@example.com'
                  value={emailCcRecipients}
                  onChange={e => setEmailCcRecipients(e.target.value)}
                />
                <div className='form-text'>
                  These addresses will be CC&apos;d on law firm emails (along with the
                  firm admin) and on internal notifications.
                </div>
              </div>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </form>
          </div>
        </div>

        <div className='card mt-4'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Weekly pending cases report</h3>
          </div>
          <div className='card-body'>
            <p className='text-muted mb-4'>
              Every Monday at 8:00am Eastern, these addresses receive a count of
              open cases in each state. Completed and rejected cases are left out.
              Separate multiple addresses with commas or new lines. Save before sending a copy now.
            </p>
            <form onSubmit={handleSave}>
              <div className='mb-3'>
                <label htmlFor='weekly-report-recipients' className='form-label'>
                  Report recipients
                </label>
                <textarea
                  id='weekly-report-recipients'
                  className='form-control'
                  rows={4}
                  placeholder='ops@example.com, admin@example.com'
                  value={reportRecipients}
                  onChange={e => setReportRecipients(e.target.value)}
                />
              </div>
              <button type='submit' className='btn btn-primary me-2' disabled={saving}>
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              <button
                type='button'
                className='btn btn-outline-primary'
                disabled={sendingReport}
                onClick={handleSendReport}
              >
                {sendingReport ? 'Sending…' : 'Send report now'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
